import { AnyAction } from 'redux';
import { opApiRequest } from 'src/api/apiRequest';
import endpoints from 'src/api/opEndpoints';
import { Store } from 'src/store/index';
import getPrintingMethod, { PrintingMethod } from 'src/store/design/selector/getPrintingMethod';
import { ThunkDispatch } from 'redux-thunk';
import { debounce } from 'lodash';
import { addClipartCategoryAction, appendClipArtSubcategoriesAction, setClipArtSearchStringAction } from '../slice';
import { AppendSubcategoriesPayload } from '../reducer/appendClipArtSubcategoriesReducer';
import getClipArtCategory from '../selector/getClipArtCategory';
import sendClipArtCategoryPostMessageOperation from './sendClipArtCategoryPostMessageOperation';
import sendClipArtCategoryAppendPostMessageOperation from './sendClipArtCategoryAppendPostMessageOperation';
import fetchClipArtsPriceOperation from './fetchClipArtsPriceOperation';
import sendClipArtSearchResultsPostMessageOperation from './sendClipArtSearchResultsPostMessageOperation';
import { IClipArt, IClipArtResultCategory } from '../types/clipArtTypes';

// Public Catalog API enums
enum PublicCatalogFileType {
  PREVIEW_DEFAULT = 'preview_default',
}

enum PublicCatalogAssetSource {
  CLIPARTS = 'cliparts',
}

// File type for preview selection
const PREVIEW_FILE_TYPE = PublicCatalogFileType.PREVIEW_DEFAULT;

// API constraints - we request one extra item to determine if there are more
const PUBLIC_CATALOG_DISPLAY_LIMIT = 20;

// Root category ID - using '0' as a sentinel value since we can't use empty string (falsy) or null (not indexable)
const ROOT_CATEGORY_ID = '0';

// Public Catalog API response types
interface PublicCatalogAssetFile {
  type: string;
  url: string;
}

interface PublicCatalogAssetCategory {
  categoryId: string;
  name: string;
  position: string;
}

interface PublicCatalogAsset {
  id: string;
  name: string;
  description: string;
  status: string;
  files: PublicCatalogAssetFile[];
  category: PublicCatalogAssetCategory;
  position: number;
  priceType: string;
  tags: string[];
  mimeType: string;
}

interface PublicCatalogCategory {
  id: string;
  name: string;
  status: string;
  position: string;
  assetSourceUid: string;
  seasonalMonth: number | null;
  seasonalDay: number | null;
  seasonalDuration: number | null;
  assets: PublicCatalogAsset[];
}

interface PublicCatalogResponseData {
  categories: PublicCatalogCategory[];
  productId: number; // Product ID for pricing, returned from backend
}

// Public Asset API response type (flat list of assets)
interface PublicAssetResponseData {
  assets: PublicCatalogAsset[];
  productId: number; // Product ID for pricing, returned from backend
}

// Fallback product ID in case API doesn't return one (should not happen)
const DEFAULT_CLIPART_PRODUCT_ID = 3914;

// Map Public Catalog asset to ClipArt
const mapToClipArt = (asset: PublicCatalogAsset, productId: number): IClipArt => {
  // Find preview URL using preview_default type
  const previewFile = asset.files.find((f) => f.type === PREVIEW_FILE_TYPE);

  return {
    clipArtId: asset.id, // Use UUID directly
    title: asset.name,
    imageUrl: previewFile?.url || '',
    productId, // Product ID from backend API
    tags: asset.tags || [],
    allowColorChange: true, // Default value, not available in new API
  };
};

// Map Public Catalog category to ClipArtCategory
const mapToClipArtCategory = (
  category: PublicCatalogCategory,
  productId: number,
  parentId: string | null = null,
): IClipArtResultCategory => ({
  clipArtCategoryId: category.id, // Use UUID directly
  title: category.name,
  clipArts: category.assets.map((asset) => mapToClipArt(asset, productId)),
  subCategories: [], // Subcategories not nested in API response
  clipArtsLoaded: true,
  imageUrl: '',
  parentClipArtCategoryId: parentId,
});

const fetchClipArts = async (
  categoryId: string | null,
  searchString: string,
  displayLimit: number,
  offset: number,
  printingMethod?: PrintingMethod,
  skipLookAhead = false,
) => {
  try {
    // Look-ahead pattern: request one extra item to determine if there are more
    // Skip look-ahead for search (we want all results) or when at max limit
    const fetchLimit = skipLookAhead ? displayLimit : Math.min(displayLimit + 1, 100);

    // Build API parameters - either fetch all categories or filter by specific UUID
    const apiParams: Record<string, any> = {
      assetSourceUids: [PublicCatalogAssetSource.CLIPARTS],
      limit: fetchLimit,
      offset,
      query: searchString,
    };

    // Only send printingMethod when the active product's method is known (apparel).
    // Omitting it preserves default catalog ordering for non-apparel products.
    if (printingMethod) {
      apiParams.printingMethod = printingMethod;
    }

    // If we have a category UUID, fetch only that specific category
    // and get more assets instead of the default preview limit (15)
    if (categoryId) {
      apiParams.categoryIds = [categoryId];
      apiParams.assetsLimit = 100; // Get more assets when viewing a specific category
    }

    const response = (await opApiRequest(
      endpoints.PUBLIC_CATALOG_V1_LIST,
      apiParams,
      'data',
      'GET',
    )) as PublicCatalogResponseData | null;

    if (!response || !response.categories || !response.categories.length) {
      return undefined;
    }

    // Get productId from API response, fallback to default
    const productId = response.productId ?? DEFAULT_CLIPART_PRODUCT_ID;

    // Look-ahead: if we got more than displayLimit, there are more items
    const hasMore = response.categories.length > displayLimit;

    // Only return up to displayLimit items (trim the extra look-ahead item)
    const categoriesToReturn = hasMore
      ? response.categories.slice(0, displayLimit)
      : response.categories;

    // If fetching a specific category by UUID, return just that category
    // Set parent to root so the back button will be shown
    if (categoryId && categoriesToReturn.length === 1) {
      return mapToClipArtCategory(categoriesToReturn[0], productId, ROOT_CATEGORY_ID);
    }

    // Create root category with subcategories
    const subCategories = categoriesToReturn.map((cat) => mapToClipArtCategory(cat, productId, ROOT_CATEGORY_ID));

    const rootCategory: IClipArtResultCategory = {
      clipArtCategoryId: ROOT_CATEGORY_ID, // Root category ID
      title: 'Cliparts',
      clipArts: [], // Root has no direct cliparts, only subcategories
      subCategories,
      clipArtsLoaded: true,
      imageUrl: '',
      parentClipArtCategoryId: null,
      hasMore, // Set hasMore flag based on look-ahead
    };

    // No recursive pagination - let the application request more with offset
    return rootCategory;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Could not fetch clip arts category, exception: ', e);
    return undefined;
  }
};

/**
 * Fetch assets directly from the public-asset API (flat list, no categories)
 * Used for search and viewing all assets in a category ("see all")
 */
const fetchAssetsDirectly = async (
  query?: string,
  categoryId?: string,
): Promise<IClipArt[]> => {
  try {
    const apiParams: Record<string, any> = {
      assetSourceUids: [PublicCatalogAssetSource.CLIPARTS],
      limit: 100,
    };

    if (query) {
      apiParams.query = query;
    }

    if (categoryId) {
      apiParams.categoryIds = [categoryId];
    }

    const response = (await opApiRequest(
      endpoints.PUBLIC_ASSET_V1_LIST,
      apiParams,
      'data',
      'GET',
    )) as PublicAssetResponseData | null;

    if (!response || !response.assets) {
      return [];
    }

    // Get productId from API response, fallback to default
    const productId = response.productId ?? DEFAULT_CLIPART_PRODUCT_ID;

    return response.assets.map((asset) => mapToClipArt(asset, productId));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Could not fetch assets directly, exception: ', e);
    return [];
  }
};

/**
 * Fetch all assets for a specific category using the public-asset API
 * Returns the category structure needed for display
 */
const fetchCategoryAllAssets = async (
  categoryId: string,
): Promise<IClipArtResultCategory | undefined> => {
  const clipArts = await fetchAssetsDirectly(undefined, categoryId);

  if (clipArts.length === 0) {
    return undefined;
  }

  return {
    clipArtCategoryId: categoryId,
    title: 'Category', // Title will be displayed from cached category data
    clipArts,
    subCategories: [],
    clipArtsLoaded: true,
    imageUrl: '',
    parentClipArtCategoryId: ROOT_CATEGORY_ID, // Back button goes to root
  };
};

const fetchSearchResults = async (
  dispatch: ThunkDispatch<Store, undefined, AnyAction>,
  clipArtCategoryId: string | null,
  searchString: string,
  requestVersion: number,
  limit: number,
) => {
  // Use the public-asset API directly for search - single API call, flat list of assets
  const clipArts = await fetchAssetsDirectly(searchString, undefined);

  // Create a result category with the flat list of clip arts
  const searchResults: IClipArtResultCategory = {
    clipArtCategoryId: ROOT_CATEGORY_ID,
    title: 'Search Results',
    clipArts,
    subCategories: [],
    clipArtsLoaded: true,
    imageUrl: '',
    parentClipArtCategoryId: null,
  };

  await dispatch(fetchClipArtsPriceOperation(searchResults));
  dispatch(sendClipArtSearchResultsPostMessageOperation(searchResults, requestVersion));
};

const fetchSearchResultsDebounced = debounce(fetchSearchResults, 500);

const fetchClipArtCategoryOperation = (clipArtCategoryId: string | null = null, searchString = '', requestVersion = 0, offset = 0) => async (
  dispatch: ThunkDispatch<Store, undefined, AnyAction>,
  getState: () => Store,
) => {
  dispatch(setClipArtSearchStringAction(searchString));
  const useSearchFlow = Boolean(searchString);
  const displayLimit = PUBLIC_CATALOG_DISPLAY_LIMIT;
  const printingMethod = getPrintingMethod(getState());
  let actualCategoryId = clipArtCategoryId;

  if (!useSearchFlow) {
    // Determine if this is a "see all" request for a specific category
    const isSpecificCategory = clipArtCategoryId !== null && clipArtCategoryId !== ROOT_CATEGORY_ID;

    // When requesting root (null), use ROOT_CATEGORY_ID as the internal ID
    const categoryIdToFetch = clipArtCategoryId ?? ROOT_CATEGORY_ID;
    const existingClipArtCategory = getClipArtCategory(getState(), categoryIdToFetch);

    // "See all" for a specific category - use direct asset API
    if (isSpecificCategory && clipArtCategoryId) {
      if (!existingClipArtCategory) {
        // Fetch all assets in this category using the public-asset API
        const categoryWithAssets = await fetchCategoryAllAssets(clipArtCategoryId);
        if (categoryWithAssets) {
          await dispatch(fetchClipArtsPriceOperation(categoryWithAssets));
          dispatch(addClipartCategoryAction(categoryWithAssets));
          actualCategoryId = categoryWithAssets.clipArtCategoryId;
          dispatch(sendClipArtCategoryPostMessageOperation(actualCategoryId, requestVersion));
        }
      } else {
        // Category already loaded, just send the existing data
        dispatch(sendClipArtCategoryPostMessageOperation(clipArtCategoryId, requestVersion));
      }
      return;
    }

    // Browsing categories (root view) - use catalog API with pagination
    // If offset > 0, we're paginating - append to existing category
    if (offset > 0 && existingClipArtCategory) {
      const additionalCategories = await fetchClipArts(clipArtCategoryId, searchString, displayLimit, offset, printingMethod);
      if (additionalCategories && additionalCategories.subCategories) {
        await dispatch(fetchClipArtsPriceOperation(additionalCategories));

        const newSubcategories = additionalCategories.subCategories;
        const hasMore = additionalCategories.hasMore ?? false;

        // Build payload with hasMore if available
        const appendPayload: AppendSubcategoriesPayload = {
          parentCategoryId: categoryIdToFetch,
          subcategories: newSubcategories,
          hasMore,
        };

        dispatch(appendClipArtSubcategoriesAction(appendPayload));
        // Send only the NEW subcategories to the editor (not the full accumulated list)
        dispatch(sendClipArtCategoryAppendPostMessageOperation(newSubcategories, hasMore, requestVersion));
      }
    } else if (!existingClipArtCategory) {
      // Initial load - fetch first page of categories
      const fullClipArtCategory = await fetchClipArts(clipArtCategoryId, searchString, displayLimit, 0, printingMethod);
      if (fullClipArtCategory) {
        await dispatch(fetchClipArtsPriceOperation(fullClipArtCategory));
        dispatch(addClipartCategoryAction(fullClipArtCategory));
        actualCategoryId = fullClipArtCategory.clipArtCategoryId;
        // Use set post message for initial load
        dispatch(sendClipArtCategoryPostMessageOperation(actualCategoryId, requestVersion));
      }
    } else {
      // Category already loaded, just send the existing data
      dispatch(sendClipArtCategoryPostMessageOperation(categoryIdToFetch, requestVersion));
    }
    return;
  }

  fetchSearchResultsDebounced(dispatch, clipArtCategoryId, searchString, requestVersion, displayLimit);
};

export default fetchClipArtCategoryOperation;
